import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import PrimaryButton from 'commons/src/components/buttons/PrimaryButton';
import Dropdown from 'commons/src/components/dropdown/MultipleAttrDropdown';
import { SensorTypes } from 'commons/src/models/commonEnums';
import { AnyDeviceType, DeviceWithKeyInfo, SegmentProperties } from 'commons/src/models/commonTypeScript';
import { Store } from '../../../reducers';
import DateToFromSelector from '../reportSelector/DateSelector';
import SelectedDeviceItem from './SelectedDeviceItem';
import useWellForm from './useWellForm';
import WellReportDeviceDropdown from './WellReportDeviceDropdown';
import styles from './WellReportForm.module.scss';

type Props = {
    acceptedDeviceTypes: AnyDeviceType[];
    possibleSensorTypes: SensorTypes[];
    minNumberOfDays: number;
    maxNumberOfDays?: number;
    optionalBuildingProps: string[];
};

const WellReportForm = ({
    acceptedDeviceTypes,
    possibleSensorTypes,
    minNumberOfDays,
    maxNumberOfDays,
    optionalBuildingProps,
}: Props): React.ReactElement => {
    const { t: txt } = useTranslation();
    const segmentProperties = useSelector((state: Store) => state.segmentPropertiesStore.segmentProperties);
    const {
        building,
        selectedDevices,
        reportStartTime,
        reportEndTime,
        displayValidation,
        errorMessage,
        isFormValid,
        hasShownSuccess,
        fetchBuildingLoading,
        fetchReportDataLoading,
        earliestSegmentStartDate,
        earliestSegmentEndDate,
        locationOptions,
        buildingName,
        setReportStartTime,
        setReportEndTime,
        setDatesAreValid,
        selectBuildingFromDropdown,
        updateDeviceSelection,
        generateReport,
    } = useWellForm({
        acceptedDeviceTypes,
        possibleSensorTypes,
        optionalBuildingProps,
    });

    const getDeviceLocation = useCallback(
        (device: DeviceWithKeyInfo): string => {
            const segmentProps: SegmentProperties | undefined = segmentProperties[device.serialNumber];
            if (!segmentProps) return '';

            const parts: string[] = [];
            if (segmentProps.floor !== undefined) {
                parts.push(`${txt('Floor')} ${segmentProps.floor}`);
            }
            if (segmentProps.roomType) {
                parts.push(txt(`RoomTypeOptions.${segmentProps.roomType}`));
            }
            return parts.join(' • ');
        },
        [segmentProperties, txt]
    );

    const removeDevice = useCallback(
        (deviceSerialNumber: string): void => {
            const updatedDevices = selectedDevices.filter(device => device.serialNumber !== deviceSerialNumber);
            updateDeviceSelection(updatedDevices);
        },
        [selectedDevices, updateDeviceSelection]
    );

    return (
        <div className="form">
            <div className={styles.sectionHeader}>
                <h3>{txt('Building.Building')}</h3>
            </div>
            <div className={`form__row ${styles.buildingDropdownContainer}`}>
                <Dropdown
                    id="buildingSelector"
                    title=""
                    options={locationOptions}
                    loading={false}
                    validate={displayValidation && !building}
                    defaultOption="Select building"
                    value={buildingName}
                    onSelect={selectBuildingFromDropdown}
                    testAttr="select-building"
                    testId="select-building"
                    fullWidth
                />
            </div>
            <div className={styles.sectionHeader}>
                <h3>{txt('Insight.TimePeriod')}</h3>
            </div>
            <div className={`form__row form__button-container ${styles.dateFieldsContainer}`}>
                <DateToFromSelector
                    segmentEndDate={earliestSegmentEndDate}
                    segmentStartDate={earliestSegmentStartDate}
                    setEndDate={setReportEndTime}
                    setStartDate={setReportStartTime}
                    startDate={reportStartTime}
                    endDate={reportEndTime}
                    minNumberOfDays={minNumberOfDays}
                    maxNumberOfDays={maxNumberOfDays}
                    setDatesAreValid={setDatesAreValid}
                    validateSelection={displayValidation || !!(reportStartTime && reportEndTime)}
                    className={styles.dateFieldRow}
                />
            </div>
            <div className={styles.sectionHeader}>
                <h3>{txt('Devices')}</h3>
            </div>
            <div className={`form__row ${styles.deviceDropdownRow}`}>
                <WellReportDeviceDropdown
                    building={building}
                    selectedDevices={selectedDevices}
                    setSelectedDevices={updateDeviceSelection}
                    acceptedDeviceTypes={acceptedDeviceTypes}
                />
            </div>
            {selectedDevices.length > 0 && (
                <div className={`form__row ${styles.selectedDevicesList}`}>
                    {selectedDevices.map(device => {
                        const location = getDeviceLocation(device);
                        return (
                            <SelectedDeviceItem
                                key={device.serialNumber}
                                device={device}
                                location={location}
                                onRemove={removeDevice}
                            />
                        );
                    })}
                </div>
            )}
            {errorMessage && (
                <div className="form__row">
                    <div className="error-message">{errorMessage}</div>
                </div>
            )}
            <div className={styles.buttonContainer}>
                <PrimaryButton
                    type="button"
                    title="GenerateReport"
                    color="primary"
                    disabled={fetchBuildingLoading || !isFormValid || hasShownSuccess}
                    onClick={generateReport}
                    loading={fetchReportDataLoading}
                    testAttr="generate-well-report"
                />
            </div>
            <div className={`form__row ${styles.emailInfoRow}`}>
                <p className="text-small">{txt('WellReport.EmailDeliveryInfo')}</p>
            </div>
        </div>
    );
};

export default WellReportForm;
